import { parsePhoneNumberFromString } from "libphonenumber-js";
import {
  type Country,
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input";

// Index every country by its dial code so we can flip the flag the moment a
// user types "+44" — well before libphonenumber-js's parser commits to a
// country. Built once at module load.
const COUNTRIES_BY_CALLING_CODE = new Map<string, Country[]>();
for (const c of getCountries()) {
  const code = getCountryCallingCode(c);
  const list = COUNTRIES_BY_CALLING_CODE.get(code) ?? [];
  list.push(c);
  COUNTRIES_BY_CALLING_CODE.set(code, list);
}

// For dial codes shared by multiple territories ("+1", "+44", etc.), pick the
// country a typical user means when they enter the code alone. Without this,
// the alphabetical fallback gives surprising results like "+1" → 🇦🇬 Antigua.
const PRIMARY_COUNTRY_FOR_CALLING_CODE: Record<string, Country> = {
  "1": "US",
  "7": "RU",
  "39": "IT",
  "44": "GB",
  "47": "NO",
  "212": "MA",
  "262": "RE",
  "290": "SH",
  "358": "FI",
  "590": "GP",
  "599": "CW",
};

/**
 * Best-effort country for a stored value. Returns undefined when the value is
 * empty or not in international (E.164) format — national-format strings are
 * ambiguous without a country hint.
 */
export function countryFromValue(value: string): Country | undefined {
  if (!value.startsWith("+")) {
    return;
  }
  try {
    return parsePhoneNumberFromString(value)?.country;
  } catch {
    return;
  }
}

/**
 * Resolve a dial code (e.g. "44") to a single country. If the user's current
 * country already uses this dial code (e.g. they're on CA and type "+1"), it's
 * preserved so we don't yank them off a deliberate choice.
 */
export function countryFromCallingCode(
  code: string,
  current: Country | undefined
): Country | undefined {
  const matches = COUNTRIES_BY_CALLING_CODE.get(code);
  if (!matches?.length) {
    return;
  }
  if (current && matches.includes(current)) {
    return current;
  }
  if (matches.length === 1) {
    return matches[0];
  }
  return PRIMARY_COUNTRY_FOR_CALLING_CODE[code] ?? matches[0];
}

/**
 * Try to parse `raw` and return both the E.164 string and the detected
 * country. Both fall back gracefully: the number stays as `raw` when partial
 * or unparseable, and the country is undefined when libphonenumber-js can't
 * commit yet (e.g. while the user is mid-type).
 */
export function parsePhone(
  raw: string,
  country: Country | undefined
): { number: string; country: Country | undefined } {
  if (!raw) {
    return { number: raw, country: undefined };
  }
  try {
    const parsed = country
      ? parsePhoneNumberFromString(raw, { defaultCountry: country })
      : parsePhoneNumberFromString(raw);
    if (parsed) {
      return { number: parsed.number ?? raw, country: parsed.country };
    }
  } catch {
    // partial or invalid input
  }
  return { number: raw, country: undefined };
}

/**
 * Convenience wrapper around {@link parsePhone} when only the E.164 string is
 * needed (e.g. blur-time normalisation).
 */
export function toE164(raw: string, country: Country | undefined): string {
  return parsePhone(raw, country).number;
}
